import {
  Component,
  State,
  Prop,
  Watch,
  h,
  ComponentInterface,
  Listen,
  getMode,
  Fragment
} from '@stencil/core';
import {
  ActivityMapRegion,
  emitClickTrackerEvent,
  jsonWatcher,
  onFocusWithTab,
  registerClickOutside
} from '../../../../utils/utils';
import clsx from 'clsx';
import { Link, SubMenuRow } from '../../ds-contextual-navigation';
import { Labels, translate } from '../../../../utils/translations';

@Component({
  tag: 'ds-contextual-navigation-submenu',
  styleUrls: {
    default: 'ds-contextual-navigation-submenu.scss',
    light: 'ds-contextual-navigation-submenu.scss'
  },
  shadow: true
})
export class ContextualNavigationSubmenu implements ComponentInterface {
  private _items: Array<SubMenuRow>;
  private _subMenuElement!: HTMLElement;
  private _subMenuButton!: HTMLElement;
  private _subMenuWrapper!: HTMLElement;
  private _subMenuListRef!: HTMLElement;
  private _submenuSideWidth: number;

  private _isAdjustable!: boolean;

  @State() open: boolean;
  @State() isLoaded: boolean = false;
  @State() submenuPositionLeft: number;
  @State() submenuPositionRight: number;
  @State() boundaryRightExceeded: boolean;
  @State() boundaryLeftExceeded: boolean;
  @State() isFocused: boolean;

  /** Navigation menu items */
  @Prop() items: Array<SubMenuRow> | string;
  /** Link that is displayed at the bottom of the menu */
  @Prop() deepLink?: Link;
  /** Title for the submenu opener */
  @Prop() name: string;
  /** (optional) Title that is displayed on top of the submenu dropdown */
  @Prop() displayTitle?: string;
  /** (optional) not used */
  @Prop() url?: string;
  /** (optional) is this submenu a Sap Explore menu */
  @Prop() exploreMenu?: boolean = false;

  /**
   * (optional) left position of the header wrapper
   * is used to calculate submenu dropdown position relative to wrapper
   */
  @Prop() boundaryLeft?: number;
  /**
   * (optional) right position of the header wrapper
   * is used to calculate submenu dropdown position relative to wrapper
   */
  @Prop() boundaryRight?: number;
  /**
   * (optional) additional padding to inset dropdown position
   */
  @Prop() boundaryPadding?: number = 0;
  /**
   * (optional) header wrapper margin for calculating outer boundaries
   */
  @Prop() headerMargin?: number = 0;

  @Watch('boundaryLeft')
  @Watch('boundaryRight')
  @Watch('headerMargin')
  calculateTranslateX() {
    if (this._subMenuElement && !this.exploreMenu) {
      if (!this.isLoaded) {
        // if isLoaded is false, it needs to be set to true to hide submenu properly
        this.isLoaded = true;
      }

      if (this._submenuSideWidth <= 0) {
        // in case the page was first loaded as mobile view and then enlarged to desktop view
        // submenuSideWidth should be calculated
        this.isLoaded = false;
        this._submenuSideWidth = this._subMenuListRef?.offsetWidth;
      }

      // padding of submenu_row__link
      const linksPadding = getMode(this) === 'light' ? 8 : 0;
      const isRTL =
        window.getComputedStyle(this._subMenuListRef).getPropertyValue('direction') === 'rtl';
      const buttonPadding =
        parseInt(
          window
            .getComputedStyle(this._subMenuButton.shadowRoot.querySelector('button'), null)
            .getPropertyValue('padding-inline-start')
        ) +
        parseInt(
          window
            .getComputedStyle(this._subMenuElement, null)
            .getPropertyValue('margin-inline-start')
        );
      const menuPadding =
        parseInt(
          window
            .getComputedStyle(this._subMenuListRef, null)
            .getPropertyValue('padding-inline-start')
        ) + linksPadding;

      const menuOffsetLeft =
        this._subMenuWrapper.getBoundingClientRect().left -
        this.boundaryLeft -
        menuPadding +
        buttonPadding;
      const menuOffsetRight =
        this.boundaryRight -
        this._subMenuWrapper.getBoundingClientRect().right -
        menuPadding +
        buttonPadding;
      const menuBoundaryRight = isRTL
        ? this.boundaryRight - menuOffsetLeft + this._submenuSideWidth
        : this.boundaryLeft + menuOffsetLeft + this._submenuSideWidth;

      this.submenuPositionLeft = Math.max(
        isRTL ? menuOffsetRight : menuOffsetLeft,
        this.boundaryPadding - this.headerMargin
      );
      this.submenuPositionRight = Math.min(
        isRTL ? menuOffsetLeft : menuOffsetRight,
        this.boundaryPadding - this.headerMargin
      );
      this.boundaryRightExceeded =
        menuBoundaryRight >= this.boundaryRight - this.boundaryPadding + this.headerMargin;
      this.boundaryLeftExceeded =
        this.boundaryRight - this._submenuSideWidth - this.boundaryPadding <=
        this.boundaryLeft + this.boundaryPadding - this.headerMargin;
    }
  }

  @Watch('items')
  private _updateItems(): void {
    this._itemsWatcher('items', this.items);
  }

  @Listen('cxsdsHeaderUpdatedBreakpoint', { target: 'body' })
  updatedBreakpointHandler() {
    this.open = false;
  }

  @Listen('blurSubmenuLink')
  blurSubmenuLinkHandler(e: CustomEvent) {
    const focusTarget = e.detail.relatedTarget as Node;
    this.open = this._subMenuListRef?.contains(focusTarget);
  }

  @Listen('keydown', { passive: true })
  accessibilityHandler(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.open) {
      this._toggleSubMenu();
      this._subMenuButton.shadowRoot.querySelector('button').focus();
      this.isFocused = true;
    }
  }

  componentWillLoad(): void {
    this._updateItems();
  }

  componentDidLoad(): void {
    this.calculateTranslateX();
    // when #col > 3, paddings and column gap should be adjustable depending on window width
    this._isAdjustable = this._items?.some((row) => row?.subMenuLinkItems?.length > 3);
    this._submenuSideWidth = this._subMenuListRef?.offsetWidth;

    registerClickOutside(this, this._subMenuWrapper, () => {
      if (this.open) {
        this.open = false;
        emitClickTrackerEvent({
          componentName: ActivityMapRegion.Header,
          title: `CNL Click Outside: ${this.name}`,
          url: ''
        });
      }
    });
    window.addEventListener('beforeunload', () => (this.open = false));
  }

  private _toggleSubMenu = () => {
    this.calculateTranslateX();
    this.open = !this.open;
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: `CNL ${this.open ? 'Open' : 'Close'}: ${this.name}`,
      url: ''
    });
  };

  private _itemsWatcher(prop: string, value: Array<SubMenuRow> | string): void {
    const resolved = jsonWatcher(prop, value);
    if (resolved) {
      this[`_${resolved.prop}`] = resolved.value;
    }
  }

  render() {
    const hasSubmenu = this.items?.length > 0;
    if (!hasSubmenu) {
      return (
        <div class='has-submenu-wrapper' data-mode={getMode(this)}>
          <p class='has-submenu'>
            <ds-link
              class={clsx('main-link', { 'main-link--focused': this.isFocused })}
              href={this.url}
              onClick={() =>
                emitClickTrackerEvent(
                  {
                    componentName: ActivityMapRegion.Header,
                    title: this.name,
                    url: this.url
                  },
                  { hasUiActions: false }
                )
              }
              onFocusin={onFocusWithTab(() => (this.isFocused = true))}
              onFocusout={() => (this.isFocused = false)}
              type='inherit'
              data-mode={getMode(this)}
              ref={(el) => (this._subMenuButton = el as HTMLElement)}
            >
              {this.name}
            </ds-link>
          </p>
        </div>
      );
    }
    return (
      <div
        class='has-submenu-wrapper'
        ref={(el) => (this._subMenuWrapper = el as HTMLElement)}
        data-mode={getMode(this)}
      >
        <div
          class={clsx(
            'has-submenu',
            { open: this.open },
            { 'has-submenu--explore': this.exploreMenu }
          )}
          ref={(el) => (this._subMenuElement = el as HTMLElement)}
        >
          <ds-navigation-button
            class={clsx('main-link', { 'main-link--focused': this.isFocused })}
            color='transparent'
            aria-label={translate(Labels['Navigation.ToggleSubMenu'])}
            accessibilityAttributes={{ expanded: this.open, hasPopup: true }}
            onClick={this._toggleSubMenu}
            onFocus={onFocusWithTab(() => (this.isFocused = true))}
            onFocusout={() => (this.isFocused = false)}
            ref={(el) => (this._subMenuButton = el as HTMLElement)}
          >
            <span class='main-link__content'>{this.name}</span>
          </ds-navigation-button>
          <div class='underline'></div>
        </div>
        <div
          class={clsx(
            'submenu-list',
            { 'submenu-list--hidden': !this.isLoaded },
            { 'submenu-list--open': this.open },
            { 'submenu-list--explore': this.exploreMenu },
            { 'submenu-list--adjustable': this._isAdjustable }
          )}
          style={
            this.boundaryRightExceeded && this.boundaryLeftExceeded
              ? {
                  insetInlineEnd: `${this.submenuPositionRight}px`,
                  insetInlineStart: `${this.boundaryPadding - this.headerMargin}px`
                }
              : this.boundaryRightExceeded
                ? { insetInlineEnd: `${this.submenuPositionRight}px` }
                : { insetInlineStart: `${this.submenuPositionLeft}px` }
          }
          ref={(el) => (this._subMenuListRef = el as HTMLElement)}
          onFocusout={(e) => {
            const focusTarget = e.relatedTarget as Node;
            this.open = this._subMenuListRef?.contains(focusTarget);
          }}
        >
          {this.displayTitle && <div class='display-title'>{this.displayTitle}</div>}
          {this._items?.map((subMenuRow) => (
            <ds-header-submenu-row
              subMenuTitle={this.name}
              subMenuRow={subMenuRow}
              isExploreMenu={this.exploreMenu}
              isAdjustable={this._isAdjustable}
            />
          ))}
          {!!this.deepLink ? (
            <ds-link
              class='submenu-list__deep-link'
              type='secondary'
              onClick={(event) =>
                emitClickTrackerEvent(
                  {
                    componentName: ActivityMapRegion.Header,
                    title: `CNL: ${this.name} > ${this.deepLink?.title}`,
                    url: this.deepLink?.url,
                    originEvent: event
                  },
                  { hasUiActions: false }
                )
              }
              href={this.deepLink?.url}
              target={this.deepLink?.externalLink ? '_blank' : null}
            >
              {this.deepLink?.title}
              {this.deepLink?.externalLink ? (
                <Fragment>
                  <span class='submenu-row__external-label'>
                    {translate(Labels['Navigation.LinkTargetBlank'])}
                  </span>
                  <ds-icon
                    aria-hidden='true'
                    name='ExternalLink'
                    class='submenu-row__external-icon'
                  />
                </Fragment>
              ) : null}
            </ds-link>
          ) : null}
        </div>
      </div>
    );
  }
}
